import { useParams, useOutletContext, Link, Navigate } from "react-router-dom";
import { useQuery } from "@tanstack/react-query";
import { Star, ArrowUpRight, ArrowLeft, TagSimple, CheckCircle } from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { buildPath, SITE_URL, canonicalSlug } from "@/lib/routes";
import { toLangSlug } from "@/lib/slug_map";
import { useLang } from "@/hooks/useLang";
import Seo from "@/components/Seo";
import PageHero from "@/components/PageHero";
import { Reveal } from "@/components/motion/Reveal";
import LeadMagnet from "@/components/LeadMagnet";
import { breadcrumbSchema } from "@/lib/schema";

export default function ToolDetail() {
  const { stack: urlStack, tool } = useParams();
  const { lang, t } = useLang();
  const { profile } = useOutletContext();
  const daStack = canonicalSlug("stack", urlStack);
  const { data: stackData, isLoading } = useQuery({ queryKey: ["stack", daStack], queryFn: () => api.getTechStack(daStack) });

  const canonicalStackSlug = toLangSlug("stack", daStack, lang);
  if (canonicalStackSlug && urlStack !== canonicalStackSlug) {
    return <Navigate to={buildPath("tool", lang, { stack: daStack, tool })} replace />;
  }

  if (isLoading || !stackData) return <div className="px-6 md:px-20 py-32 text-muted-foreground">…</div>;
  const toolData = (stackData.tools || []).find((x) => x.slug === tool);
  const sc = stackData[lang] || stackData.da;
  if (!toolData) return <div className="px-6 md:px-20 py-32 text-muted-foreground">{t("not_found")}</div>;

  const tc = toolData[lang] || toolData.da;
  const ex = toolData.extra?.[lang];
  const isFree = ["gratis", "free"].includes((tc.pricing || "").trim().toLowerCase());
  const stack = daStack;
  const path = buildPath("tool", lang, { stack: daStack, tool });
  const others = (stackData.tools || []).filter((x) => x.slug !== tool).slice(0, 4);

  const appSchema = {
    "@context": "https://schema.org",
    "@type": "SoftwareApplication",
    name: toolData.name,
    applicationCategory: toolData.category,
    aggregateRating: { "@type": "AggregateRating", ratingValue: toolData.rating, bestRating: 5, ratingCount: 1 },
    review: {
      "@type": "Review",
      author: { "@id": `${SITE_URL}/#person` },
      reviewRating: { "@type": "Rating", ratingValue: toolData.rating, bestRating: 5 },
    },
    url: `${SITE_URL}${path}`,
  };

  return (
    <>
      <Seo routeKey="tool" params={{ stack, tool }} lang={lang}
        title={`${toolData.name} \u2013 ${lang === "da" ? "sådan bruger jeg det" : "how I use it"} | Magnus Løv`}
        description={tc.description}
        jsonLd={[appSchema, breadcrumbSchema([
          { name: t("nav_home"), path: buildPath("home", lang) },
          { name: t("nav_techstacks"), path: buildPath("techstacks", lang) },
          { name: sc.title, path: buildPath("techstack", lang, { slug: stack }) },
          { name: toolData.name, path },
        ])]} />

      <PageHero title={toolData.name} description={tc.description}
        crumbs={[
          { label: t("nav_home"), to: "home" },
          { label: t("nav_techstacks"), to: "techstacks" },
          { label: sc.title, to: "techstack", params: { slug: stack } },
          { label: toolData.name },
        ]} />

      <section className="px-6 md:px-12 lg:px-20 py-16">
        <div className="grid gap-14 lg:grid-cols-3">
          <div className="lg:col-span-2 space-y-12">
            {ex?.how_i_use && (
              <Reveal>
                <div>
                  <h2 className="font-heading text-2xl md:text-3xl font-bold tracking-tight">
                    {lang === "da" ? `Sådan bruger jeg ${toolData.name}` : `How I use ${toolData.name}`}
                  </h2>
                  <p className="mt-4 text-lg text-muted-foreground font-light leading-relaxed">{ex.how_i_use}</p>
                </div>
              </Reveal>
            )}
            {ex?.best_for && (
              <Reveal>
                <div className="border-t border-border pt-8">
                  <h2 className="mb-4 font-heading text-2xl md:text-3xl font-bold tracking-tight">
                    {lang === "da" ? "Bedst til" : "Best for"}
                  </h2>
                  <div className="flex items-start gap-3">
                    <CheckCircle size={22} weight="fill" className="mt-0.5 shrink-0 text-foreground" />
                    <p className="text-muted-foreground font-light leading-relaxed">{ex.best_for}</p>
                  </div>
                </div>
              </Reveal>
            )}

            {/* Discount section (placeholder — hidden for free tools) */}
            {!isFree && (
            <Reveal>
              <div data-testid="tool-discount" className="rounded-2xl border border-dashed border-border bg-secondary/40 p-8">
                <div className="flex items-center gap-2 text-muted-foreground">
                  <TagSimple size={20} weight="fill" />
                  <span className="label-mono">{toolData.name} + {lang === "da" ? "rabat" : "discount"}</span>
                </div>
                <h3 className="mt-4 font-heading text-xl font-bold">
                  {lang === "da" ? "Rabat på vej" : "Discount on the way"}
                </h3>
                <p className="mt-3 max-w-xl text-sm text-muted-foreground font-light leading-relaxed">
                  {lang === "da"
                    ? `Jeg arbejder på at skaffe en eksklusiv rabat eller fordel på ${toolData.name} til jer, der finder det via min side. Er den klar, står den her. Indtil da kan du besøge ${toolData.name} direkte nedenfor.`
                    : `I'm working on securing an exclusive discount or perk on ${toolData.name} for those who find it via my site. When it's ready, it'll appear here. Until then, you can visit ${toolData.name} directly below.`}
                </p>
              </div>
            </Reveal>
            )}
          </div>

          <div>
            <Reveal>
              <div className="sticky top-24 space-y-4">
                <div className="rounded-xl border border-border bg-card p-7">
                  <div className="flex h-12 w-12 items-center justify-center rounded-lg border border-border bg-secondary font-heading text-xl font-black">
                    {toolData.name.charAt(0)}
                  </div>
                  <div className="mt-5 label-mono text-muted-foreground/60">{lang === "da" ? "Kategori" : "Category"}</div>
                  <div className="mt-1 text-sm">{toolData.category}</div>
                  <div className="mt-4 label-mono text-muted-foreground/60">{lang === "da" ? "Vurdering" : "Rating"}</div>
                  <div className="mt-1 flex items-center gap-1">
                    {[...Array(5)].map((_, i) => (
                      <Star key={i} size={14} weight="fill" className={i < Math.round(toolData.rating) ? "text-foreground" : "text-border"} />
                    ))}
                    <span className="ml-1 font-mono text-xs text-muted-foreground">{toolData.rating.toFixed(1)}</span>
                  </div>
                  <div className="mt-4 label-mono text-muted-foreground/60">{lang === "da" ? "Pris" : "Price"}</div>
                  <div className="mt-1 text-sm">{tc.pricing}</div>
                  <a href={toolData.url} target="_blank" rel="noopener noreferrer sponsored" data-testid="tool-visit"
                    className="group mt-7 inline-flex w-full items-center justify-center gap-2 rounded-md bg-primary px-5 py-3.5 font-medium text-primary-foreground transition-transform hover:scale-[1.03]">
                    {t("visit")} {toolData.name}
                    <ArrowUpRight size={16} weight="bold" className="transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" />
                  </a>
                  <p className="mt-3 text-center font-mono text-[10px] text-muted-foreground/50">
                    {lang === "da" ? "Kan indeholde affiliate-link" : "May contain an affiliate link"}
                  </p>
                </div>
              </div>
            </Reveal>
          </div>
        </div>
      </section>

      {/* Other tools in this stack */}
      <section className="border-t border-border px-6 md:px-12 lg:px-20 py-16">
        <div className="mb-6 flex items-center justify-between">
          <div className="label-mono text-muted-foreground/60">{lang === "da" ? `Mere i ${sc.title}` : `More in ${sc.title}`}</div>
          <Link to={buildPath("techstack", lang, { slug: stack })} className="inline-flex items-center gap-1.5 text-sm text-muted-foreground hover:text-foreground">
            <ArrowLeft size={14} /> {sc.title}
          </Link>
        </div>
        <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-4">
          {others.map((o) => (
            <Link key={o.slug} to={buildPath("tool", lang, { stack, tool: o.slug })} data-testid={`related-tool-${o.slug}`}
              className="card-hover group flex items-center justify-between rounded-xl border border-border bg-card p-5">
              <span className="font-heading font-bold">{o.name}</span>
              <ArrowUpRight size={16} className="text-muted-foreground group-hover:text-foreground" />
            </Link>
          ))}
        </div>
      </section>
      <LeadMagnet profile={profile} />
    </>
  );
}
